"use client";

import { cn } from "../../lib/utils";
import React from "react";

interface AnimatedCircularProgressBarProps {
  max?: number;
  min?: number;
  value?: number;
  gaugePrimaryColor?: string;
  gaugeSecondaryColor?: string;
  className?: string;
}

export function AnimatedCircularProgressBar({
  max = 100,
  min = 0,
  value = 0,
  gaugePrimaryColor,
  gaugeSecondaryColor,
  className,
}: AnimatedCircularProgressBarProps) {
  const circumference = 2 * Math.PI * 45;
  const percentPx = circumference / 100;
  const currentPercent = ((value - min) / (max - min)) * 100;

  return (
    <div
      className={cn(
        "relative size-40 text-[var(--font-size-2xl)] font-[var(--font-weight-semibold)]",
        className,
      )}
      style={
        {
          "--circle-size": "100px",
          "--circumference": circumference,
          "--percent-to-px": `${percentPx}px`,
          "--gap-percent": "5",
          "--offset-factor": "0",
          "--transition-length": "1s",
          "--transition-step": "200ms",
          "--delay": "0s",
          "--percent-to-deg": "3.6deg",
          transform: "translateZ(0)",
        } as React.CSSProperties
      }
    >
      <svg
        fill="none"
        className="size-full"
        strokeWidth="2"
        viewBox="0 0 100 100"
      >
        {gaugePrimaryColor && (
          <defs>
            <linearGradient
              id={`linear-${gaugePrimaryColor.replace(/[^a-zA-Z0-9]/g, "")}`}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor={gaugePrimaryColor} />
              <stop offset="100%" stopColor={gaugePrimaryColor} />
            </linearGradient>
          </defs>
        )}
        <circle
          cx="50"
          cy="50"
          r="45"
          stroke={gaugeSecondaryColor}
          className="opacity-90"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          stroke={gaugePrimaryColor || `url(#linear-${gaugePrimaryColor?.replace(/[^a-zA-Z0-9]/g, "")})`}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (currentPercent / 100) * circumference}
          className="transition-all duration-700 ease-in-out"
        />
      </svg>
      <span
        data-current-value={currentPercent}
        className="duration-&lsqb;var(--transition-length)&rsqb; delay-&lsqb;var(--delay)&rsqb; absolute inset-0 m-auto size-fit ease-linear animate-in fade-in"
      >
        {Math.round(currentPercent)}%
      </span>
    </div>
  );
}